import { useCallback, useState } from "react";

/**
 * Avatar data shape from API
 */
interface AvatarData {
  id: number;
  storagePath?: string;
  fileName?: string;
}

/**
 * Avatar size variants
 */
type AvatarSize = "sm" | "md" | "lg";

/**
 * Size configuration map
 */
const SIZE_CONFIG: Record<AvatarSize, { container: string; text: string }> = {
  sm: {
    container: "w-8 h-8",
    text: "text-xs",
  },
  md: {
    container: "w-12 h-12",
    text: "text-lg",
  },
  lg: {
    container: "w-16 h-16",
    text: "text-2xl",
  },
};

/**
 * Avatar component props
 */
interface AvatarProps {
  /** Avatar data from API - { id, storagePath? } or undefined */
  avatar?: AvatarData | null;
  /** Fallback initials (e.g. "JD", "AB") - required if no avatar */
  fallback?: string;
  /** Size variant: sm (32px), md (48px), lg (64px) */
  size?: AvatarSize;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Reusable Avatar component with image support and initials fallback.
 *
 * @example
 * // With avatar image
 * <Avatar avatar={{ id: 1 }} fallback="JD" />
 *
 * @example
 * // With initials fallback only
 * <Avatar fallback="AB" size="lg" />
 *
 * @example
 * // No avatar data
 * <Avatar fallback="?" />
 */
export function Avatar({ avatar, fallback = "?", size = "md", className = "" }: AvatarProps) {
  const [imgError, setImgError] = useState(false);

  const sizeConfig = SIZE_CONFIG[size];

  // Avatars are served through the authenticated-safe public endpoint (/api/public firewall)
  // Uploads are never exposed as static files (see infra nginx.conf: deny all on /uploads/)
  const avatarUrl = avatar?.id
    ? `${import.meta.env.VITE_PUBLIC_URL}/api/public/avatars/${avatar.id}`
    : null;

  // Determine if we should show image or fallback
  const showImage = avatarUrl && !imgError;

  const handleImageError = useCallback(() => {
    setImgError(true);
  }, []);

  return (
    <div
      className={`relative inline-flex items-center justify-center ${sizeConfig.container} overflow-hidden ${className}`}
      style={{ borderRadius: "9999px" }}
    >
      {showImage ? (
        <img
          src={avatarUrl}
          alt={fallback}
          className="w-full h-full object-cover"
          onError={handleImageError}
        />
      ) : (
        <div className="bg-gradient-to-br from-primary to-primary/80 text-primary-content w-full h-full flex items-center justify-center">
          <span className={`${sizeConfig.text} font-bold leading-none`} title={fallback}>
            {fallback}
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * Get initials from a name (first letters of first two words)
 *
 * @example
 * getInitials("John Doe") // "JD"
 * getInitials("Jean Dupont") // "JD"
 * getInitials("Alice") // "A"
 * getInitials("") // "?"
 */
export function getInitials(name: string): string {
  if (!name || name.trim().length === 0) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

export default Avatar;
